import React, { useState, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { UpdateProduct } from '../../../assets/interfaces/productInterface'
import { GetPrices } from '../../../assets/interfaces/priceInterface'
import { MenuItem, FormControl, Select, InputLabel, Snackbar, Modal, Button, Alert, Typography } from '@mui/material'
import CustomTable from '../customTable/customTable'
import SyncPriceButton from '../../global/SyncPriceButton'
import './productTabs.css'
import { buildExportData } from './exportUtils'

function ProductTabs(props) {
    const { Products } = props
    const [SelectedProduct, setSelectedProduct] = useState(null)
    const [Prices, setPrices] = useState([])
    const [currency, setCurrency] = useState('EUR')
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
    const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (props.SelectedIndex >= 0 && Products[props.SelectedIndex]) {
            const product = Products[props.SelectedIndex]
            setSelectedProduct(product)
            setCurrency(product.currency?.trim() || 'EUR')
            loadPrices(product.id)
        }
    }, [props.SelectedIndex, Products])

    const loadPrices = (productId) => {
        setLoading(true)
        try {
            GetPrices(productId, (prices) => {
                setPrices(prices)
                setLoading(false)
            })
        } catch (error) {
            console.error('Error loading prices:', error)
            setLoading(false)
        }
    }

    const handleCurrencyChange = (event) => {
        const newCurrency = event.target.value
        setCurrency(newCurrency)
        if (SelectedProduct) {
            const updatedProduct = { ...SelectedProduct, currency: newCurrency }
            UpdateProduct(SelectedProduct.id, updatedProduct)
                .then((success) => {
                    if (success) {
                        setSnackbar({ open: true, message: 'Currency updated successfully!', severity: 'success' })
                        if (props.onProductUpdate) {
                            props.onProductUpdate(updatedProduct)
                        }
                    } else {
                        setSnackbar({ open: true, message: 'Failed to update currency.', severity: 'error' })
                    }
                })
                .catch((error) => {
                    console.error('Error updating currency:', error)
                    setSnackbar({
                        open: true,
                        message: 'An error occurred while updating currency.',
                        severity: 'error',
                    })
                })
        }
    }

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return
        setSnackbar((prev) => ({ ...prev, open: false }))
    }

    function onExportXLS() {
        if (!SelectedProduct || !Prices || Prices.length === 0) {
            setSnackbar({ open: true, message: 'No data to export', severity: 'warning' })
            return
        }
        try {
            const { titleRow, headerRow, dataRows } = buildExportData(Prices, SelectedProduct)
            const worksheet = XLSX.utils.aoa_to_sheet([titleRow, headerRow, ...dataRows])
            const workbook = XLSX.utils.book_new()

            XLSX.utils.book_append_sheet(workbook, worksheet)
            XLSX.writeFile(workbook, `${SelectedProduct.name || 'prices'}_export.xlsx`)

            setSnackbar({ open: true, message: 'Export successful!', severity: 'success' })
        } catch (error) {
            console.error('Error exporting:', error)
            setSnackbar({ open: true, message: 'Export failed: ' + error.message, severity: 'error' })
        }
    }

    if (!SelectedProduct) {
        return <div>Loading...</div>
    }

    return (
        <div className='TabsContainer'>
            <div className='TabsHeader'>
                {Products?.length > 1 &&
                    Products.map((product, index) => (
                        <div
                            key={product.id}
                            id={`product_tab_${props.builder}_${index}`}
                            onClick={() => props.OnSelectedProductChange(index)}
                            className={'Tab ' + (props.SelectedIndex === index ? 'SelectedTab' : '')}>
                            <span>{product.description}</span>
                        </div>
                    ))}
            </div>

            <div className='SelectedTabContainer'>
                {Products[0].name && (
                    <div>
                        <h2>Actions</h2>
                        <div>
                            <span>
                                <span
                                    className={'PrimaryButton'}
                                    id={`export_xls_${props.builder}`}
                                    onClick={onExportXLS}>
                                    Export to XLS
                                </span>
                            </span>
                            <span
                                id={`delete_product_${props.builder}`}
                                onClick={props.onDeleteProduct}
                                className='SecondaryWarningButton'>
                                Delete Product
                            </span>
                            <span
                                id={`change_currency_${props.builder}`}
                                onClick={() => setIsCurrencyModalOpen(true)}
                                className='PrimaryButton'>
                                $ Change Currency
                            </span>
                            <span>
                                <SyncPriceButton
                                    builder={props.builder}
                                    product={SelectedProduct.name}
                                    onSyncComplete={() => loadPrices(SelectedProduct.id)}
                                    initialMessage={`Sync Prices (${SelectedProduct.name})`}
                                    buttonProps={{
                                        color: 'primary',
                                        variant: 'outlined',
                                        className: 'SyncButton',
                                    }}
                                />
                            </span>
                        </div>
                    </div>
                )}

                {Prices && Prices.length > 0 ? (
                    <div>
                        <h2>List Prices</h2>
                        <CustomTable
                            Prices={Prices}
                            ProductId={SelectedProduct.id}
                            onPricesUpdate={(updatedPrices) => setPrices(updatedPrices)}
                            currency={currency}
                            loading={loading}
                        />
                    </div>
                ) : (
                    <Typography variant='body1' sx={{ mt: 2 }}>
                        No prices available for this product
                    </Typography>
                )}
            </div>

            <Modal
                open={isCurrencyModalOpen}
                onClose={() => setIsCurrencyModalOpen(false)}
                aria-labelledby='currency_modal_title'
                className='CurrencyModal'>
                <div className='CurrencyModalContent'>
                    <h2 id='currency_modal_title' className='CurrencyModalTitle'>
                        Configure Currency
                    </h2>
                    <FormControl variant='filled' className='formcontrol' fullWidth>
                        <InputLabel>Currency</InputLabel>
                        <Select value={currency} onChange={handleCurrencyChange}>
                            <MenuItem value='EUR'>€ EUR</MenuItem>
                            <MenuItem value='USD'>$ USD</MenuItem>
                        </Select>
                    </FormControl>
                    <div className='CurrencyModalActions'>
                        <Button variant='contained' color='primary' onClick={() => setIsCurrencyModalOpen(false)}>
                            Close
                        </Button>
                    </div>
                </div>
            </Modal>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert elevation={6} variant='filled' onClose={handleSnackbarClose} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default ProductTabs
